import getUser from '../getUser';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public statusText: string,
    public body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** True for 401 / 403 responses */
  get isAuthError(): boolean {
    return this.status === 401 || this.status === 403;
  }

  /** True for 404 responses */
  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** True for 5xx responses */
  get isServerError(): boolean {
    return this.status >= 500;
  }
}

export interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  /** Automatically JSON-stringify and set Content-Type (default: true for objects) */
  json?: boolean;
  /** Request body – objects are JSON-stringified automatically */
  body?: RequestInit['body'] | Record<string, unknown> | unknown[];
  /** Request timeout in ms (default: 30 000) */
  timeout?: number;
  /** Skip the Authorization header for this request */
  skipAuth?: boolean;
}

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

const BASE_URL = process.env.REACT_APP_API_ENDPOINT ?? '';

/** Override the default token getter (e.g. for tests or non-React contexts) */
let _getToken: () => string | undefined = () => getUser()?.access_token;

export function configureApi(opts: { getToken?: () => string | undefined }) {
  if (opts.getToken) _getToken = opts.getToken;
}

// ---------------------------------------------------------------------------
// Core request function
// ---------------------------------------------------------------------------

async function request<T = unknown>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const {
    json = true,
    body,
    timeout = 30_000,
    skipAuth = false,
    headers: extraHeaders,
    ...fetchOpts
  } = options;

  // Build URL – support both absolute URLs and relative paths
  const url = path.startsWith('http') ? path : `${BASE_URL}${path}`;

  // Build headers
  const headers = new Headers(extraHeaders);

  if (!skipAuth) {
    const token = _getToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  // Serialise body
  let resolvedBody: RequestInit['body'] | undefined;

  if (body !== undefined && body !== null) {
    const isPlainObject =
      typeof body === 'object' &&
      !(body instanceof FormData) &&
      !(body instanceof Blob) &&
      !(body instanceof ArrayBuffer) &&
      !(body instanceof URLSearchParams) &&
      !(body instanceof ReadableStream);

    if (json && isPlainObject) {
      resolvedBody = JSON.stringify(body);
      if (!headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
      }
    } else {
      resolvedBody = body as RequestInit['body'];
    }
  } else if (!headers.has('Content-Type') && ['POST', 'PUT', 'PATCH'].includes(fetchOpts.method?.toUpperCase() ?? '')) {
    // For methods that typically send bodies, set json content type even without body
    headers.set('Content-Type', 'application/json');
  }

  // Timeout via AbortController
  const controller = new AbortController();
  const timeoutId = timeout > 0 ? setTimeout(() => controller.abort(), timeout) : undefined;

  try {
    const response = await fetch(url, {
      ...fetchOpts,
      headers,
      body: resolvedBody,
      signal: controller.signal,
    });

    if (!response.ok) {
      let errorBody: unknown;
      try {
        errorBody = await response.json();
      } catch {
        try {
          errorBody = await response.text();
        } catch {
          // ignore
        }
      }

      throw new ApiError(
        `${response.status} ${response.statusText}: ${fetchOpts.method ?? 'GET'} ${path}`,
        response.status,
        response.statusText,
        errorBody,
      );
    }

    // 204 No Content
    if (response.status === 204) {
      return undefined as T;
    }

    // Try JSON first; fall back to text
    const contentType = response.headers.get('Content-Type') ?? '';
    if (contentType.includes('application/json')) {
      return (await response.json()) as T;
    }

    return (await response.text()) as unknown as T;
  } catch (error) {
    if (error instanceof ApiError) throw error;

    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError(`Request timed out after ${timeout}ms: ${fetchOpts.method ?? 'GET'} ${path}`, 408, 'Request Timeout');
    }

    throw error;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

// ---------------------------------------------------------------------------
// Convenience methods
// ---------------------------------------------------------------------------

export const api = {
  get<T = unknown>(path: string, opts?: ApiRequestOptions) {
    return request<T>(path, { ...opts, method: 'GET' });
  },

  post<T = unknown>(path: string, body?: ApiRequestOptions['body'], opts?: ApiRequestOptions) {
    return request<T>(path, { ...opts, method: 'POST', body });
  },

  put<T = unknown>(path: string, body?: ApiRequestOptions['body'], opts?: ApiRequestOptions) {
    return request<T>(path, { ...opts, method: 'PUT', body });
  },

  patch<T = unknown>(path: string, body?: ApiRequestOptions['body'], opts?: ApiRequestOptions) {
    return request<T>(path, { ...opts, method: 'PATCH', body });
  },

  delete<T = unknown>(path: string, opts?: ApiRequestOptions) {
    return request<T>(path, { ...opts, method: 'DELETE' });
  },

  /** Raw request when you need full control */
  request,
} as const;

export default api;
